/**
 * Grand Quack Auto — neon night system.
 *
 * Blade Runner 2049 vibes: glowing road edges, duck aura, street lights,
 * and lit building windows that fade in at dusk. MeshBasicMaterial objects
 * bloom through the existing UnrealBloomPass — no extra render passes.
 */

import * as THREE from 'three';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import {
  CITY_BLOCK_GRID, GROUND_SIZE, NEON, POST_PROCESSING, ROAD_WIDTH,
} from '../config';
import type { System, World } from '../types';
import { getCycleT } from './dayNightSystem';
import { buildingDefs } from '../world/cityGenerator';

const GRID = CITY_BLOCK_GRID;
const STRIDE = 16; // CITY_BLOCK_SIZE + ROAD_WIDTH
const roadCenter = (i: number): number => (i - GRID / 2) * STRIDE;

let initialized = false;
let roadStrips: THREE.InstancedMesh | null = null;
let windowMesh: THREE.InstancedMesh | null = null;
let duckLight: THREE.PointLight | null = null;
let streetLights: THREE.PointLight[] = [];
let bloomPass: UnrealBloomPass | null = null;

/** Calculate night intensity: 0 during day, 1 at full night. */
function nightFactor(t: number): number {
  const { duskStart, duskEnd, dawnStart, dawnEnd } = NEON;
  if (t >= duskEnd && t < dawnStart) return 1;
  if (t >= duskStart && t < duskEnd) return (t - duskStart) / (duskEnd - duskStart);
  if (t >= dawnStart && t < dawnEnd) return 1 - (t - dawnStart) / (dawnEnd - dawnStart);
  if (t < duskStart) return 0;
  return 0;
}

function init(world: World): void {
  initialized = true;
  // Find bloom pass in composer.
  if (world.composer) {
    for (const p of (world.composer as unknown as { passes: unknown[] }).passes) {
      if (p instanceof UnrealBloomPass) { bloomPass = p; break; }
    }
  }
  createRoadStrips(world);
  createDuckLight(world);
  createStreetLights(world);
  createWindows(world);
}

function createRoadStrips(world: World): void {
  const count = (GRID + 1) * 2 * 2; // 7 roads × 2 directions × 2 edges
  const geom = new THREE.BoxGeometry(1, NEON.stripHeight, 1);
  const mat = new THREE.MeshBasicMaterial({ color: 0x00ffff });
  roadStrips = new THREE.InstancedMesh(geom, mat, count);
  roadStrips.name = 'neonStrips';
  const m = new THREE.Matrix4();
  const pos = new THREE.Vector3();
  const q = new THREE.Quaternion();
  const scl = new THREE.Vector3();
  const c = new THREE.Color();
  let idx = 0;
  const hw = ROAD_WIDTH / 2 - 0.2; // inset from road edge
  for (let i = 0; i <= GRID; i++) {
    const rc = roadCenter(i);
    const col = NEON.stripColors[i % NEON.stripColors.length]!;
    // Horizontal road edges (along X).
    for (const side of [-1, 1]) {
      pos.set(0, NEON.stripHeight / 2 + 0.02, rc + side * hw);
      scl.set(GROUND_SIZE, 1, NEON.stripWidth);
      m.compose(pos, q, scl); roadStrips.setMatrixAt(idx, m);
      c.setHex(col); roadStrips.setColorAt(idx, c); idx++;
    }
    // Vertical road edges (along Z).
    for (const side of [-1, 1]) {
      pos.set(rc + side * hw, NEON.stripHeight / 2 + 0.02, 0);
      scl.set(NEON.stripWidth, 1, GROUND_SIZE);
      m.compose(pos, q, scl); roadStrips.setMatrixAt(idx, m);
      c.setHex(col); roadStrips.setColorAt(idx, c); idx++;
    }
  }
  roadStrips.instanceMatrix.needsUpdate = true;
  roadStrips.instanceColor!.needsUpdate = true;
  roadStrips.visible = false;
  world.scene.add(roadStrips);
}

function createDuckLight(world: World): void {
  duckLight = new THREE.PointLight(NEON.duckGlowColor, 0, NEON.duckGlowDistance);
  duckLight.position.set(0, 1.5, 0);
  world.duck.add(duckLight);
}

function createStreetLights(world: World): void {
  const intersections: [number, number][] = [];
  for (let i = 1; i < GRID; i++)
    for (let j = 1; j < GRID; j++) intersections.push([roadCenter(i), roadCenter(j)]);
  // Shuffle and pick maxStreetLights.
  for (let i = intersections.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [intersections[i], intersections[j]] = [intersections[j]!, intersections[i]!];
  }
  const count = Math.min(NEON.maxStreetLights, intersections.length);
  for (let i = 0; i < count; i++) {
    const [x, z] = intersections[i]!;
    const col = NEON.streetColors[i % NEON.streetColors.length]!;
    const light = new THREE.PointLight(col, 0, NEON.streetDistance);
    light.position.set(x, NEON.streetHeight, z);
    world.scene.add(light);
    streetLights.push(light);
  }
}

function createWindows(world: World): void {
  if (buildingDefs.length === 0) return;
  const perBuilding = NEON.windowsPerFace * 2; // front + back faces
  const total = Math.min(buildingDefs.length * perBuilding, 600);
  const geom = new THREE.PlaneGeometry(NEON.windowSize, NEON.windowSize);
  const mat = new THREE.MeshBasicMaterial({ color: 0xffffff, side: THREE.DoubleSide });
  windowMesh = new THREE.InstancedMesh(geom, mat, total);
  windowMesh.name = 'neonWindows';
  const m = new THREE.Matrix4();
  const pos = new THREE.Vector3();
  const qF = new THREE.Quaternion();
  const qS = new THREE.Quaternion().setFromEuler(new THREE.Euler(0, Math.PI / 2, 0));
  const scl = new THREE.Vector3(1, 1, 1);
  const c = new THREE.Color();
  let idx = 0;
  for (const b of buildingDefs) {
    if (idx >= total) break;
    for (let f = 0; f < 2 && idx < total; f++) {
      const face = f === 0 ? qF : qS; // front (Z) or side (X)
      for (let w = 0; w < NEON.windowsPerFace && idx < total; w++) {
        const wx = b.x + (f === 0 ? (Math.random() - 0.5) * (b.w - 1) : (f === 0 ? 1 : -1) * b.w / 2);
        const wz = b.z + (f === 1 ? (Math.random() - 0.5) * (b.d - 1) : (f === 0 ? 1 : -1) * b.d / 2);
        const wy = 1.5 + Math.random() * (b.h - 3);
        pos.set(wx, wy, wz);
        m.compose(pos, face, scl); windowMesh.setMatrixAt(idx, m);
        c.setHex(NEON.windowColors[Math.floor(Math.random() * NEON.windowColors.length)]!);
        windowMesh.setColorAt(idx, c); idx++;
      }
    }
  }
  windowMesh.count = idx;
  windowMesh.instanceMatrix.needsUpdate = true;
  windowMesh.instanceColor!.needsUpdate = true;
  windowMesh.visible = false;
  world.scene.add(windowMesh);
}

export const neonSystem: System = {
  name: 'neon',
  render(_dt: number, _alpha: number, world: World) {
    if (!initialized) init(world);
    const nf = nightFactor(getCycleT());
    const isNight = nf > 0.01;
    // Road strips.
    if (roadStrips) { roadStrips.visible = isNight; }
    // Windows.
    if (windowMesh) { windowMesh.visible = isNight; }
    // Duck glow.
    if (duckLight) duckLight.intensity = nf * NEON.duckGlowIntensity;
    // Street lights.
    for (const l of streetLights) l.intensity = nf * NEON.streetIntensity;
    // Bloom: lerp from day settings to night neon settings.
    if (bloomPass) {
      bloomPass.strength = THREE.MathUtils.lerp(POST_PROCESSING.bloomStrength, NEON.nightBloomStrength, nf);
      bloomPass.threshold = THREE.MathUtils.lerp(POST_PROCESSING.bloomThreshold, NEON.nightBloomThreshold, nf);
      bloomPass.radius = THREE.MathUtils.lerp(POST_PROCESSING.bloomRadius, NEON.nightBloomRadius, nf);
    }
  },
};
